import { ProductImageComponent } from '../shared/product-image/product-image.component'
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  HostListener,
  afterNextRender,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core'
import {
  DOCUMENT,
  Location,
  NgTemplateOutlet,
  ViewportScroller,
} from '@angular/common'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import { HttpErrorResponse } from '@angular/common/http'
import { Subscription } from 'rxjs'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MarketplaceService } from '../../services/marketplace.service'
import { RouteService } from '../../services/route.service'
import { ROUTES } from '../../constants/routes'
import { TranslationService } from '../../services/translation.service'
import { MarketplaceProduct } from '../../types/marketplace'
import { ProductCustomFieldsComponent } from '../product-custom-fields/product-custom-fields.component'

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-marketplace-detail',
  imports: [
    ProductImageComponent,
    ProductCustomFieldsComponent,
    NgTemplateOutlet,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './marketplace-detail.component.html',
  styleUrl: './marketplace-detail.component.scss',
})
export class MarketplaceDetailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private location = inject(Location)
  private scroller = inject(ViewportScroller)
  private document = inject(DOCUMENT)
  private injector = inject(Injector)
  marketplace = inject(MarketplaceService)
  private routeService = inject(RouteService)
  t = inject(TranslationService)
  product?: MarketplaceProduct
  loading = true
  failed = false
  notFound = false
  components: MarketplaceProduct[] = []
  componentsLoading = false
  componentsFailed = false
  quantityDrafts: Record<string, string> = {}
  invalidQuantityId?: string
  parentProduct?: { id: string; name: string }
  cartActionId?: string
  // Where to return once the listing and its component cards are back on the page.
  private restorePosition?: [number, number]
  private restoreComponents = false
  private restoreFocusProductId?: string
  // Disable the previous/next buttons when the horizontal card row reaches either end.
  railAtStart = true
  railAtEnd = true
  private routeSubscription?: Subscription
  private request?: Subscription
  private componentsRequest?: Subscription

  /**
   * Loads the cart and follows product ID changes on the details route.
   * @returns {void} No return value.
   */
  ngOnInit(): void {
    this.marketplace.loadCart()
    this.routeService.setRoute(
      this.routeService.getUserRoutes(
        ROUTES.MARKETPLACE.title,
        ROUTES.MARKETPLACE.path,
      ),
    )
    this.routeSubscription = this.route.paramMap.subscribe((params) =>
      this.load(params.get('id')),
    )
  }

  /**
   * Saves the current page position and cancels this page's subscriptions.
   * @returns {void} No return value.
   */
  ngOnDestroy(): void {
    this.rememberPosition()
    this.routeSubscription?.unsubscribe()
    this.request?.unsubscribe()
    this.componentsRequest?.unsubscribe()
  }

  /**
   * Loads a listing and its direct components, replacing any previous requests.
   * @param {string | null} [id] Product ID; defaults to the current route's ID.
   * @returns {void} No return value.
   */
  load(id = this.route.snapshot.paramMap.get('id')): void {
    this.rememberPosition()
    const navigation = this.router.currentNavigation()
    // Browser Back/Forward should return to the component the user opened.
    this.restoreFocusProductId =
      navigation?.trigger === 'popstate' ? this.product?.id : undefined
    // Component links carry their parent so we can offer a direct way back.
    const state = navigation?.extras.state ?? this.location.getState()
    const parent = (
      state as { marketplaceParent?: { id?: unknown; name?: unknown } }
    ).marketplaceParent
    this.parentProduct =
      typeof parent?.id === 'string' &&
      typeof parent?.name === 'string' &&
      parent.id !== id
        ? { id: parent.id, name: parent.name }
        : undefined
    this.restorePosition =
      id && navigation?.trigger === 'popstate'
        ? this.marketplace.detailScrollPositions.get(id)
        : undefined
    this.restoreComponents =
      !this.restorePosition &&
      this.route.snapshot.fragment === 'components-heading'
    this.scroller.scrollToPosition([0, 0])
    this.request?.unsubscribe()
    this.componentsRequest?.unsubscribe()
    this.product = undefined
    this.components = []
    this.componentsLoading = false
    this.componentsFailed = false
    this.quantityDrafts = {}
    this.invalidQuantityId = undefined
    this.cartActionId = undefined
    if (!id) {
      this.loading = false
      this.failed = true
      this.notFound = true
      return
    }
    this.loading = true
    this.failed = false
    this.notFound = false
    this.request = this.marketplace.get(id).subscribe({
      next: (product) => {
        this.product = product
        this.loading = false
        if (!this.restorePosition && !this.restoreComponents) this.restoreView()
        this.loadComponents()
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false
        this.failed = true
        this.notFound = error.status === 404
        this.restoreComponents = false
        this.restorePosition = undefined
        this.restoreFocusProductId = undefined
        this.restoreView('detail-error')
      },
    })
  }

  /**
   * Loads visible direct components without blocking use of the main listing.
   * @returns {void} No return value.
   */
  loadComponents(): void {
    if (!this.product) return
    this.componentsRequest?.unsubscribe()
    this.componentsLoading = true
    this.componentsFailed = false
    this.componentsRequest = this.marketplace
      .getComponents(this.product.id)
      .subscribe({
        next: (products) => {
          this.components = products
          this.componentsLoading = false
          if (this.restorePosition || this.restoreComponents) this.restoreView()
          // Wait for Angular to put the cards on the page before measuring the row.
          afterNextRender(() => this.updateRailState(), {
            injector: this.injector,
          })
        },
        error: () => {
          this.componentsLoading = false
          this.componentsFailed = true
          if (this.restorePosition || this.restoreComponents) this.restoreView()
        },
      })
  }

  /**
   * Saves the listing's page position for a later Back/Forward navigation.
   * @returns {void} No return value.
   */
  private rememberPosition(): void {
    if (this.product)
      this.marketplace.detailScrollPositions.set(
        this.product.id,
        this.scroller.getScrollPosition(),
      )
  }

  /**
   * Restores the saved card focus and page position, or moves to a requested heading.
   * @param {string} [target] Fallback element ID; defaults to the product heading.
   * @returns {void} No return value.
   */
  private restoreView(target = 'product-heading'): void {
    const id = this.product?.id
    // The heading and cards must exist before we can focus or scroll to them.
    afterNextRender(
      () => {
        if (id !== this.product?.id) return
        const anchor = this.restoreComponents ? 'components-heading' : target
        const returnLink = this.restoreFocusProductId
          ? Array.from(
              this.document.querySelectorAll<HTMLAnchorElement>(
                '.component-info h3 a',
              ),
            ).find(
              (link) =>
                link.getAttribute('href') ===
                '/marketplace/' + this.restoreFocusProductId,
            )
          : undefined
        returnLink?.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'instant',
        })
        const focusTarget = returnLink ?? this.document.getElementById(anchor)
        focusTarget?.focus({
          preventScroll: true,
        })
        if (this.restoreComponents) this.scroller.scrollToAnchor(anchor)
        else this.scroller.scrollToPosition(this.restorePosition ?? [0, 0])
        this.restoreComponents = false
        this.restorePosition = undefined
        this.restoreFocusProductId = undefined
      },
      { injector: this.injector },
    )
  }

  /**
   * Checks the horizontal card row and updates which arrow buttons are available.
   * @returns {void} No return value.
   */
  @HostListener('window:resize')
  updateRailState(): void {
    const rail = this.document.getElementById('component-rail')
    this.railAtStart = !rail || rail.scrollLeft <= 1
    this.railAtEnd =
      !rail || rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1
  }

  /**
   * Moves the component row by one card, including the gap between cards.
   * @param {-1 | 1} direction -1 for the previous card, 1 for the next card.
   * @param {Event} [event] Keyboard event; ignored when a control inside the row has focus.
   * @returns {void} No return value.
   */
  scrollComponents(direction: -1 | 1, event?: Event): void {
    const rail = this.document.getElementById('component-rail')
    if (!rail || (event && event.target !== rail)) return
    event?.preventDefault()
    const width =
      rail.querySelector('.component-card')?.getBoundingClientRect().width ??
      rail.clientWidth
    const gap =
      parseFloat(
        this.document.defaultView?.getComputedStyle(rail).columnGap ?? '16',
      ) || 16
    const reduceMotion = this.document.defaultView?.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    rail.scrollBy({
      left: direction * (width + gap),
      behavior: reduceMotion ? 'instant' : 'smooth',
    })
  }

  /**
   * Moves focus and the page to the Components heading from the summary shortcut.
   * @param {Event} event Link activation whose default navigation is replaced.
   * @returns {void} No return value.
   */
  showComponents(event: Event): void {
    event.preventDefault()
    this.document
      .getElementById('components-heading')
      ?.focus({ preventScroll: true })
    this.scroller.scrollToAnchor('components-heading')
  }

  /**
   * Supplies the parent listing for a component's Back to product link.
   * @returns {{ marketplaceParent: { id: string; name: string } } | undefined} Parent route state, or undefined before the listing loads.
   */
  componentNavigation():
    { marketplaceParent: { id: string; name: string } } | undefined {
    return this.product
      ? { marketplaceParent: { id: this.product.id, name: this.product.name } }
      : undefined
  }

  /**
   * Reads a product's saved buyer quantity from the cart.
   * @param {string} id Product ID to look up.
   * @returns {number} Saved request quantity, or zero when the product is absent.
   */
  cartQuantity(id: string): number {
    return (
      this.marketplace.cartItems().find((item) => item.productId === id)
        ?.quantity ?? 0
    )
  }

  /**
   * Checks whether a product's cart change is still being saved or refreshed.
   * @param {string} id Product ID whose saving state is needed.
   * @returns {boolean} Whether to show this product's saving message.
   */
  saving(id: string): boolean {
    return (
      this.marketplace.pendingId() === id ||
      (this.cartActionId === id && this.marketplace.cartLoading())
    )
  }

  /**
   * Previews the opening sentence, shortening long text at a word boundary.
   * @returns {string} Description preview, or an empty string when no description exists.
   */
  summary(): string {
    const description =
      this.product?.description?.trim().replace(/\s+/g, ' ') ?? ''
    const sentence =
      description.match(/^.*?[.!?](?:\s|$)/u)?.[0].trim() ?? description
    if (sentence.length <= 220) return sentence
    const boundary = sentence.lastIndexOf(' ', 220)
    return sentence.slice(0, boundary > 0 ? boundary : 220) + '…'
  }

  /**
   * Checks whether the main product has any values for the Specifications section.
   * @returns {boolean} Whether the section has content to display.
   */
  hasSpecifications(): boolean {
    const p = this.product
    return (
      !!p &&
      !!(
        p.marketplaceCategory ||
        p.marketplaceSubcategory ||
        p.condition ||
        p.measureValue !== null ||
        p.droneFlightTimeMinutes !== null ||
        p.droneType ||
        p.droneVideoResolution ||
        p.droneSensorSize ||
        p.droneFpv !== null
      )
    )
  }

  /**
   * Formats a listing's unit price using its currency and the browser's locale.
   * @param {MarketplaceProduct} [product] Listing to price; defaults to the main product.
   * @returns {string} Formatted price, or the translated price-not-provided message.
   */
  price(product = this.product): string {
    if (!product || product.priceAmount === null)
      return this.t.getMarketplacePriceNotProvidedText()
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: product.priceCurrency ?? 'CAD',
      currencyDisplay: 'code',
    }).format(product.priceAmount)
  }

  /**
   * Adds or removes one product using its buyer-entered request quantity.
   * @param {MarketplaceProduct} [product] Listing to toggle; defaults to the main product.
   * @returns {void} No return value.
   */
  select(product = this.product): void {
    if (!product) return
    if (this.marketplace.pendingId() || this.marketplace.cartLoading()) return
    if (this.marketplace.isSelected(product.id)) {
      this.cartActionId = product.id
      this.marketplace.toggleSelection(product)
      return
    }
    const draft = this.quantityDrafts[product.id] ?? '1'
    const quantity = Number(draft)
    if (
      !draft.trim() ||
      !this.marketplace.validQuantity(quantity, product.requestUnit)
    ) {
      this.invalidQuantityId = product.id
      return
    }
    this.invalidQuantityId = undefined
    this.cartActionId = product.id
    this.marketplace.toggleSelection(product, quantity)
  }

  /**
   * Stores the typed request quantity and clears that product's validation message.
   * @param {string} id Product ID associated with the input.
   * @param {Event} event Input event carrying the new quantity text.
   * @returns {void} No return value.
   */
  quantityChanged(id: string, event: Event): void {
    this.quantityDrafts[id] = (event.target as HTMLInputElement).value
    if (this.invalidQuantityId === id) this.invalidQuantityId = undefined
  }
}
